/**
 * Reserved-sheet catalog — client-owned descriptors for the sheets
 * the server doesn't synthesise. The client seeds an ephemeral
 * RawSheet from this catalog and CoW-promotes on first edit.
 *
 * Flow:
 *   1. `SheetModelCache.open(":~lona:home")` calls `backend.fetchSheet`.
 *   2. Server returns `null` if no persisted record exists for this
 *      user's `:~lona:home`.
 *   3. Accessor falls through to
 *      `ephemeralFromCatalog(":~lona:home", <signed-in user id>)` which
 *      hydrates a `RawSheet` whose sheet id is derived from the catalog key
 *      NAMESPACED BY THE OWNER, so two accounts never mint the same global
 *      `spreadsheets.id`. No DB contact.
 *   4. First edit triggers `SheetHandle.ensurePromoted()` →
 *      `POST /api/sheets` with each row carrying its `lookupKey`.
 *      Server-side preflight fails 409 if a second tab promoted
 *      first.
 *
 * Row templates stay minimal — `type`, `label`, `lookupKey`, and
 * (for pomodoro) `attributes.description`. Anything more elaborate
 * (icons, attributes.scale, etc.) lives on the server today and
 * can migrate in follow-ups.
 */

import type { Option } from "../core/option.ts";
import type { JsonReader } from "@tento-core/json-reader";

import type { SheetsNetwork } from "../wire/rows-network.ts";
import type { RawSheet } from "./raw-sheet.ts";
import { hydrateSheetDefinition } from "../sheet/sheet-definition.ts";
import { deepFrozen } from "../core/frozen-collections.ts";

/** Minimal row shape the catalog emits. Widened to `RawRow` at
 *  catalog-instantiation time (`ephemeralFromCatalog`). */
interface CatalogRow {
  readonly type: string;
  /**
   * Reserved-row data address this row reconnects to on promote
   * (`:~lona:weather`, `:~lona:tasks`, …). Omitted for pure container
   * aliases that own no reserved data of their own — the calendar
   * alias is one: its events flow through the nested `calendar-list`
   * child's `:~lona:calendar:primary:events` batch key, not the
   * container row.
   */
  readonly lookupKey?: string;
  readonly label?: string;
  readonly timeScale?: string;
  readonly heightPx?: number;
  readonly attributes?: JsonReader.Object;
  readonly meta?: JsonReader.Object;
  readonly children?: readonly CatalogRow[];
}

interface CatalogEntry {
  readonly title: string;
  readonly width?: number;
  readonly timeScale?: SheetsNetwork.TimeScale;
  readonly rows: readonly CatalogRow[];
}

const WEATHER: CatalogRow = {
  type: "alias",
  lookupKey: ":~lona:weather",
  attributes: {
    // `alias.default-weather` reads the user's preferred
    // location from `CalendarPreferences.live.defaultWeatherLocation`
    // at expansion time and re-expands every instance when the
    // preference changes. The template ships via the app-side
    // `defaultWeatherAliasFactory`; the rows-side stays
    // preferences-agnostic. See `preferences-aliases.ts`.
    template: "alias.default-weather",
  },
};
const TASK: CatalogRow = {
  type: "tasks",
  lookupKey: ":~lona:tasks",
  label: "Tasks",
  meta: { shape: "task/v1", encoding: "task.v1" },
};
// The full calendar. Seeded as the registered `alias.calendar` row type
// (not generic `type:"alias"` + `attributes.template`) so hydrate runs
// BOTH the descriptor `childSpecs` pass and the `alias.calendar` template
// pass — the former produces the cell-bindable descendant the latter
// alone does not, and the idempotent guard in `raw-sheet.ts` dedupes the
// overlapping subtree. See `.learnings/2026-06-15-alias-childspec-double-expansion.toml`.
// Carries no `lookupKey`: its events come from the nested `calendar-list`
// child's `:~lona:calendar:primary:events` batch key (`row-descriptor.ts`
// `CalendarList.dataOwner`), so the container binds to no reserved row.
const CALENDAR: CatalogRow = {
  type: "alias.calendar",
  label: "Calendar",
};
// `:calendar` weather lives in the sticky header — authored DIRECTLY under an
// authored header `surface`. We own this builtin sheet, so the weather has no
// body parent to hoist from; projection is only for the all-day strip (whose
// parent `alias.calendar` IS in the body). `SurfaceAnchor.of` reads
// `attributes.surface: "header"` as the authored header anchor, and the
// `alias.calendar` all-day projection grafts onto that same anchor. `:home`'s
// shared `WEATHER` authors the same alias as a plain body row; same
// preference-driven `:~lona:weather` location.
const HEADER_SURFACE: CatalogRow = {
  type: "surface",
  attributes: { surface: "header" },
  children: [
    {
      type: "alias",
      lookupKey: ":~lona:weather",
      attributes: { template: "alias.default-weather" },
    },
  ],
};
// `:~lona:chores` is a second lona-native task list (server-backed by the
// `Chores` row in lona-rows; reuses the `task.v1` shape). It renders and
// persists exactly like `:~lona:tasks` — writes go to
// `POST /api/rows/:~lona:chores/cells`.
const CHORES: CatalogRow = {
  type: "tasks",
  lookupKey: ":~lona:chores",
  label: "Chores",
  meta: { shape: "task/v1", encoding: "task.v1" },
};
const POMODORO_TIMER: CatalogRow = {
  type: "data",
  lookupKey: ":~lona:calendar:pomodoro:events",
  label: "Pomodoro",
  attributes: { description: "Track completed Pomodoro timer sessions" },
};
const JOURNAL: CatalogRow = {
  type: "journal",
  lookupKey: ":~lona:journal",
  label: "Journal",
  timeScale: "day",
  heightPx: 400,
};
const JOURNAL_SHEET_WIDTH_PX = 300;
const MOOD: CatalogRow = {
  type: "number",
  label: "Mood",
  timeScale: "day",
  attributes: { unit: "scale1-5", prompt: "rate your mood (1-5)" },
};
const WAKE_UP_TIME: CatalogRow = {
  type: "text",
  lookupKey: ":~lona:daily-habits:wake-up-time",
  label: "Wake up time",
  timeScale: "day",
  heightPx: 160,
  attributes: { prompt: "what time did you wake up?" },
};
const MEDITATION: CatalogRow = {
  type: "checkmark",
  lookupKey: ":~lona:daily-habits:meditation",
  label: "Meditation",
  timeScale: "day",
};
const MORNING_WALK: CatalogRow = {
  type: "checkmark",
  lookupKey: ":~lona:daily-habits:morning-walk",
  label: "Morning walk",
  timeScale: "day",
};
const WATER_INTAKE: CatalogRow = {
  type: "number",
  lookupKey: ":~lona:daily-habits:water-intake",
  label: "Water intake",
  timeScale: "day",
  attributes: {
    unit: "glasses",
    prompt: "how many glasses of water did you drink?",
  },
};
const EXERCISE: CatalogRow = {
  type: "text",
  lookupKey: ":~lona:daily-habits:exercise",
  label: "Exercise",
  timeScale: "day",
  attributes: { prompt: "what exercise did you do today?" },
};
// `:chart-debug` — an isolated harness for the vertical chart marks: a
// standalone `timeline[column]` timed grid holding three `alias.chart` series
// side by side, each on a distinct synthetic seed (`GraphData.Generated`), so
// the marks render with no backend. Each series composes the `regression-band`
// + `regression-curve` + `points` marks (see `chartChildren` in
// `builtin-alias-factories.ts`).
const CHART_DEBUG_SERIES = (label: string, seed: number): CatalogRow => ({
  type: "alias",
  label,
  attributes: {
    template: "alias.chart",
    seed,
    width: "64px",
  },
});
const CHART_DEBUG_TIMELINE: CatalogRow = {
  type: "timeline[column]",
  children: [
    CHART_DEBUG_SERIES("Series A", 1),
    CHART_DEBUG_SERIES("Series B", 7),
    CHART_DEBUG_SERIES("Series C", 13),
  ],
};
/** The built-in sheet templates by lookup key (`:~lona:home`, …): what `client.openBuiltin(name)` opens and a catalog sheet's default timescale. */
export const BUILTIN_SHEET_CATALOG: Readonly<Record<string, CatalogEntry>> = deepFrozen({
  // `:~lona:home` is the sole canonical address for the home sheet — the key
  // the home card (`HomeStore`), the ephemeral-row seeder, the sidebar
  // home-tile filter, navigation (`onNavigateHome`), and the server
  // (`NewSheet` "Sole address for reserved sheets") all use. The server
  // persists home sheets only under this key and returns `null` until the user
  // promotes (forks) one, falling through to this catalog so an UNFORKED home
  // still renders (a missing entry threw `sheet not found: :~lona:home`).
  ":~lona:home": { title: "Home", timeScale: "day", rows: [WEATHER, TASK] },

  ":calendar": {
    title: "Calendar",
    rows: [CALENDAR, HEADER_SURFACE],
  },
  ":chart-debug": {
    title: "Chart Debug",
    timeScale: "week",
    rows: [CHART_DEBUG_TIMELINE],
  },
  ":journal": {
    title: "Journal",
    width: JOURNAL_SHEET_WIDTH_PX,
    timeScale: "day",
    rows: [JOURNAL, MOOD],
  },
  ":daily-habits": {
    title: "Daily Habits",
    timeScale: "day",
    rows: [
      WAKE_UP_TIME,
      MEDITATION,
      MORNING_WALK,
      WATER_INTAKE,
      EXERCISE,
    ],
  },
  ":tasks": { title: "Tasks", rows: [TASK, CHORES] },
  ":debug-day": {
    title: "Debug Day",
    timeScale: "day",
    rows: [],
  },
  ":debug-pomodoro": {
    title: "Debug Pomodoro",
    timeScale: "day",
    rows: [POMODORO_TIMER],
  },
});

/** True when `lookupKey` is a builtin/reserved catalog sheet's key
 *  (`:~lona:home`, `:calendar`, `:tasks`, …). The catalog is keyed by those
 *  reserved lookup keys — authored names, not minted ids — so any text may be
 *  asked (a sheet's `lookupKey`, the key a caller opens by). These sheets are
 *  always present in the sidebar, so callers suppress the "unsaved" ephemeral
 *  disclaimer for them even while they're still ephemeral (pre-promote). */
export function isBuiltinSheetKey(lookupKey: Option<string>): boolean {
  return lookupKey != null && lookupKey in BUILTIN_SHEET_CATALOG;
}

/** Build an ephemeral `RawSheet` from the catalog entry for `lookupKey` (a
 *  reserved catalog key such as `:~lona:home`) by
 *  hydrating it through the shared `hydrateSheetDefinition` — a builtin is
 *  simply a `SheetDefinition` whose reserved `lookupKey` is its catalog key.
 *  Row ids are deterministic (stable selection / deep-link identity across
 *  opens); the sheet id is deterministic PER PRINCIPAL.
 *
 *  `ownerScope` is the principal the promoted sheet will belong to — the
 *  client registry's `ownerScope` (`RowLocalId.Registry`): the signed-in user
 *  id, or before login the client's own session scope. It namespaces
 *  the derived sheet id: `spreadsheets.id` is a global primary key, so an
 *  unscoped derivation hands every account on the deployment the SAME id and
 *  the first one to promote squats it for everybody else (see the identity
 *  invariant in `sheet-definition.ts`).
 *
 *  `null` if `lookupKey` isn't in the catalog. */
export function ephemeralFromCatalog(
  lookupKey: string,
  ownerScope: string,
): Option<RawSheet> {
  const entry = BUILTIN_SHEET_CATALOG[lookupKey];
  if (entry == null) return null;
  // The catalog key IS the reserved lookupKey → both the origin key (seeds the
  // derived sheet id + every row id) and the definition's `lookupKey`, which
  // the hydrator stamps onto the sheet so the builtin identity survives the
  // promote round trip.
  return hydrateSheetDefinition({ ...entry, lookupKey }, {
    originKey: lookupKey,
    ownerScope,
  });
}
